"use client"

import React from "react"

import { useState } from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { doctorLogin, ApiError } from "@/lib/api"
import auth from "@/lib/auth"
import { Eye, EyeOff, Mail, Lock, Sparkles, ArrowRight } from "lucide-react"
import { getSocket } from "@/lib/socket"

export function LoginForm() {
  const [loginMethod, setLoginMethod] = useState<"email" | "phone">("email")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [phone, setPhone] = useState("")
  const [otp, setOtp] = useState("")
  const [otpSent, setOtpSent] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const router = useRouter()

  const handleDoctorLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const identifier = loginMethod === "email" ? email : phone;

      const response = await doctorLogin({
        identifier,
        password,
      });

      // Handle 403 business case (pending approval, rejected, etc.)
      if (!response?.data?.accessToken) {
        setError(response.message || "Your account is pending admin approval");
        return;
      }

      auth.loginAsDoctor(
        response.data.accessToken,
        Number(response.data.expiresIn) || 3600,
        response.data.doctor
      );
      console.log(response.data,"response");
      
      const socket = getSocket(response.data.accessToken);
      socket.connect();
      socket.emit('join', {
        userId: response.data.doctor.id,
        role: 'doctor',
        token: response.data.accessToken,
      });

      router.push("/doctor/dashboard");

    } catch (err: any) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };


  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    // Simulate sending OTP
    await new Promise((resolve) => setTimeout(resolve, 1000))
    setIsLoading(false)
    setOtpSent(true)
  }

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    // Simulate OTP verification
    await new Promise((resolve) => setTimeout(resolve, 1000))
    setIsLoading(false)
    // Redirect to dashboard
    router.push("/doctor/dashboard")
  }

  const handleSubmit = (e: React.FormEvent) => {
    if (loginMethod === "email") {
      handleDoctorLogin(e)
    } else {
      handleVerifyOtp(e)
    }
  }

  return (
    <div className="space-y-6">
      {/* Login Method Tabs */}
      <div className="flex gap-2 p-1 bg-muted rounded-lg">
        <button
          type="button"
          onClick={() => {
            setLoginMethod("email")
            setOtpSent(false)
          }}
          className={`flex-1 py-2.5 px-4 rounded-md font-medium text-sm transition-all ${loginMethod === "email"
            ? "bg-primary text-primary-foreground shadow-md"
            : "text-muted-foreground hover:text-foreground"
            }`}
        >
          Email
        </button>
        <button
          type="button"
          onClick={() => {
            setLoginMethod("phone")
            setOtpSent(false)
          }}
          className={`flex-1 py-2.5 px-4 rounded-md font-medium text-sm transition-all ${loginMethod === "phone"
            ? "bg-primary text-primary-foreground shadow-md"
            : "text-muted-foreground hover:text-foreground"
            }`}
        >
          Phone
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 text-sm text-red-500 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-800">
          {error}
        </div>
      )}

      {/* Email Login Form */}
      {loginMethod === "email" && (
        <form onSubmit={handleDoctorLogin} className="space-y-5">
          <div>
            <Label htmlFor="email" className="text-sm font-medium text-foreground">Email</Label>
            <div className="relative mt-1.5">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-4 w-4 text-muted-foreground" />
              </div>
              <Input
                id="email"
                type="email"
                placeholder="doctor@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11 pl-10 bg-background border-border focus:border-primary focus:ring-primary"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-sm font-medium text-foreground">Password</Label>
              <Link
                href="/doctor/forgot-password"
                className="text-sm text-primary hover:underline font-medium"
              >
                Forgot password?
              </Link>
            </div>
            <div className="relative mt-1.5">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-4 w-4 text-muted-foreground" />
              </div>
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11 pl-10 pr-10 bg-background border-border focus:border-primary focus:ring-primary"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                )}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-medium text-base transition-all hover:shadow-lg hover:shadow-primary/25"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Signing in...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                Sign in
                <ArrowRight className="w-4 h-4" />
              </span>
            )}
          </Button>
        </form>
      )}

      {/* Phone OTP Login Form */}
      {loginMethod === "phone" && (
        <form onSubmit={otpSent ? handleVerifyOtp : handleSendOtp} className="space-y-5">
          <div>
            <Label htmlFor="phone" className="text-sm font-medium text-foreground">Phone number</Label>
            <div className="relative mt-1.5">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-4 w-4 text-muted-foreground" />
              </div>
              <Input
                id="phone"
                type="tel"
                placeholder="+1 (555) 000-0000"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                disabled={otpSent}
                className="h-11 pl-10 bg-background border-border focus:border-primary focus:ring-primary"
              />
            </div>
          </div>

          {otpSent && (
            <div>
              <Label htmlFor="otp" className="text-sm font-medium text-foreground">Enter OTP</Label>
              <Input
                id="otp"
                type="text"
                placeholder="000000"
                value={otp}
                onChange={(e) => setOtp(e.target.value.slice(0, 6))}
                maxLength={6}
                required
                className="h-12 mt-1.5 text-center text-2xl tracking-widest font-mono bg-background border-border focus:border-primary focus:ring-primary"
              />
              <p className="text-xs text-muted-foreground text-center mt-2">
                We sent a 6-digit code to your phone
              </p>
              <button
                type="button"
                onClick={() => setOtpSent(false)}
                className="text-xs text-primary hover:underline mt-2 block w-full text-center"
              >
                Change phone number
              </button>
            </div>
          )}

          <Button
            type="submit"
            className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-medium text-base transition-all hover:shadow-lg hover:shadow-primary/25"
            disabled={isLoading || (otpSent && otp.length !== 6)}
          >
            {isLoading ? (otpSent ? "Verifying..." : "Sending OTP...") : (otpSent ? "Verify OTP" : "Send OTP")}
          </Button>
        </form>
      )}

      

      <p className="text-center text-sm text-muted-foreground">
        {"Don't have an account? "}
        <Link href="/doctor/signup" className="text-primary hover:underline font-medium">
          Sign up
        </Link>
      </p>
    </div>
  )
}

